import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { bmxurl, getAccess } from 'src/globals';
import { GlobalService } from '../_services/global.service';
import { BusyService } from '../_services/busy.service';
import { cBookingfee } from 'src/types/bookingfee';

@Component({
  selector: 'app-bookingfee',
  templateUrl: './bookingfee.component.html',
  styleUrls: ['./bookingfee.component.css']
})
export class BookingfeeComponent implements OnInit {

  VenueID: string = localStorage.getItem('SmartVenueID')!;
  AccessAllowed: boolean = false;
  CompletedLoading: boolean = false;
  httpResponse: any;

  // ─── Fees list ──────────────────────────────────────────────────────────
  listBookingFees: any[] = [];
  isDisabled: boolean = true;

  // ─── Edit ────────────────────────────────────────────────────────────────
  editingFee: boolean = false;
  BookingFee!: cBookingfee;
  stateOptions = [{ label: 'Yes', value: true }, { label: 'No', value: false }];
  stateOptionsType = [
    { label: 'Fixed',      value: 'FIXED'      },
    { label: 'Per Person', value: 'PERPERSON'  },
    { label: 'Percentage', value: 'PERCENTAGE' },
  ];
  publicTextValid = true;
  publicMinMaxValid = true;
  adminMinMaxValid = true;
  showDefaultWarning = false;
  existingDefaultName = '';

  // ─── Packages tab ───────────────────────────────────────────────────────
  packages: any[] = [];
  feeDropdownOptions: { label: string; value: string | null }[] = [];
  private feeColourMap = new Map<string, string>();
  private feeNameMap  = new Map<string, string>();

  CurrencySymbol: string = localStorage.getItem('Currency') ?? 'GBP';
  CurrencyMarker: string = (() => {
    const code = localStorage.getItem('Currency') ?? 'GBP';
    if (code === 'USD') return '$';
    if (code === 'EUR') return '€';
    return '£';
  })();

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private globalService: GlobalService,
    private busyService: BusyService
  ) {}

  ngOnInit(): void {
    this.globalService.setSmallScreenMenu(false);
    this.resetFee();

    const userid = localStorage.getItem('SmartUserID');
    this.http.get(bmxurl + 'User/' + userid).subscribe({
      next: (response: any) => {
        this.httpResponse = response;
        if (this.httpResponse.succeeded) {
          if (getAccess('BOOKINGFEES', this.httpResponse.data)) {
            this.AccessAllowed = true;
          }
        }
        this.CompletedLoading = true;
        this.loadFees(false);
        this.loadPackages();
      },
      error: () => {
        this.AccessAllowed = true;
        this.CompletedLoading = true;
        this.loadFees(false);
        this.loadPackages();
      }
    });
  }

  // ─── Fees list ──────────────────────────────────────────────────────────

  toggleDisabled(): void {
    this.loadFees(!this.isDisabled);
  }

  loadFees(showDisabled: boolean): void {
    this.http.get(bmxurl + 'BookingFeeList/' + this.VenueID + '/' + showDisabled).subscribe({
      next: (response: any) => {
        if (response.succeeded) {
          this.listBookingFees = response.data ?? [];
          this.feeColourMap.clear();
          this.feeNameMap.clear();
          this.listBookingFees.forEach((f: any) => {
            this.feeColourMap.set(f.bfid, f.colour || '#4CAF50');
            this.feeNameMap.set(f.bfid, f.name + (f.isdefault ? ' (Default)' : ''));
          });
          this.feeDropdownOptions = [
            { label: 'No booking fee', value: null },
            ...this.listBookingFees.map((f: any) => ({
              label: f.name + (f.isdefault ? ' (Default)' : ''),
              value: f.bfid,
            }))
          ];
        } else {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error getting Booking Fees' });
          this.feeDropdownOptions = [{ label: 'No booking fee', value: null }];
        }
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error getting Booking Fees' });
        this.feeDropdownOptions = [{ label: 'No booking fee', value: null }];
      }
    });
  }

  feeDisplay(type: string, fee: number): string {
    if (type === 'FIXED')      return this.CurrencyMarker + fee.toFixed(2) + ' fixed';
    if (type === 'PERPERSON')  return this.CurrencyMarker + fee.toFixed(2) + ' per person';
    if (type === 'PERCENTAGE') return fee + '% percentage';
    return '';
  }

  disableData(bfid: string): void {
    if (!confirm('Are you sure you want to disable this Booking Fee?')) return;
    this.http.delete(bmxurl + 'BookingFee/' + this.VenueID + '/' + bfid).subscribe({
      next: (response: any) => {
        if (response.succeeded) {
          this.messageService.add({ severity: 'success', summary: 'Disabled', detail: 'Booking Fee successfully disabled' });
          this.loadFees(this.isDisabled);
        } else {
          this.messageService.add({ severity: 'error', summary: 'Not Disabled', detail: response.messages?.[0] ?? 'Error disabling' });
        }
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Not Disabled', detail: 'An error occurred disabling this Booking Fee' });
      }
    });
  }

  // ─── Edit ────────────────────────────────────────────────────────────────

  private resetFee(): void {
    this.BookingFee = {
      id: null, venueid: this.VenueID, disabled: false, bfid: null,
      name: '', colour: '#4CAF50', isdefault: false,
      bfpublicapply: false, bfadminapply: false,
      bfpublicshowtext: false, bfpublictext: '',
      bfpublictype: 'FIXED', bfadmintype: 'FIXED',
      bfpublicfee: 0, bfpublicmin: 0, bfpublicmax: 0,
      bfadminfee: 0, bfadminmin: 0, bfadminmax: 0,
      bookingfeeappliestomemberspublic: false,
      bookingfeeappliedonmembershippurchasespublic: false,
      bookingfeeappliestomembersadmin: false,
      bookingfeeappliedonmembershippurchasesadmin: false,
    } as unknown as cBookingfee;
  }

  addNew(): void {
    this.resetFee();
    this.publicTextValid = true;
    this.publicMinMaxValid = true;
    this.adminMinMaxValid = true;
    this.editingFee = true;
  }

  editData(bfid: string): void {
    this.http.get(bmxurl + 'bookingfee/' + this.VenueID + '/' + bfid).subscribe({
      next: (response: any) => {
        if (response.succeeded) {
          this.BookingFee = { ...response.data, colour: response.data.colour || '#4CAF50' };
          this.publicTextValid = true;
          this.publicMinMaxValid = true;
          this.adminMinMaxValid = true;
          this.editingFee = true;
        } else {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error while getting Booking Fee' });
        }
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error while getting Booking Fee' });
      }
    });
  }

  backToList(): void {
    this.editingFee = false;
    this.loadFees(!this.isDisabled);
  }

  updateData(): void {
    if (!this.doValidCheck()) return;

    if (this.BookingFee.bfadmintype === 'FIXED') { this.BookingFee.bfadminmin = 0; this.BookingFee.bfadminmax = 0; }
    if (this.BookingFee.bfpublictype === 'FIXED') { this.BookingFee.bfpublicmin = 0; this.BookingFee.bfpublicmax = 0; }

    if (this.BookingFee.isdefault) {
      this.http.get(bmxurl + 'BookingFeeList/' + this.VenueID + '/false').subscribe({
        next: (response: any) => {
          const list: any[] = response.data ?? [];
          const existingDefault = list.find(f => f.isdefault && f.bfid !== this.BookingFee.bfid);
          if (existingDefault) {
            this.existingDefaultName = existingDefault.name || existingDefault.bfid;
            this.showDefaultWarning = true;
          } else {
            this.saveBookingFee();
          }
        },
        error: () => this.saveBookingFee()
      });
    } else {
      this.saveBookingFee();
    }
  }

  confirmReplaceDefault(): void {
    this.showDefaultWarning = false;
    this.saveBookingFee();
  }

  private saveBookingFee(): void {
    const isNew = this.BookingFee.id == null;
    const request = isNew
      ? this.http.post(bmxurl + '/bookingfee', this.BookingFee)
      : this.http.put(bmxurl + '/bookingfee', this.BookingFee);

    request.subscribe({
      next: (response: any) => {
        if (response.succeeded) {
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Booking Fee successfully saved' });
          this.backToList();
        } else {
          this.messageService.add({ severity: 'error', summary: 'Not Saved', detail: response.messages?.[0] ?? 'Save failed' });
        }
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Not Saved', detail: 'Error saving Booking Fee' });
      }
    });
  }

  doValidCheck(): boolean {
    this.publicTextValid = true;
    this.publicMinMaxValid = true;
    this.adminMinMaxValid = true;
    let allValid = true;

    if (this.BookingFee.bfpublicshowtext && this.BookingFee.bfpublictext === '') {
      this.publicTextValid = false; allValid = false;
    }
    if (this.BookingFee.bfadminapply && this.BookingFee.bfadminmin > this.BookingFee.bfadminmax) {
      this.adminMinMaxValid = false; allValid = false;
    }
    if (this.BookingFee.bfpublicapply && this.BookingFee.bfpublicmin > this.BookingFee.bfpublicmax) {
      this.publicMinMaxValid = false; allValid = false;
    }
    return allValid;
  }

  // ─── Packages ────────────────────────────────────────────────────────────

  loadPackages(): void {
    this.http.get(bmxurl + 'packagelist/' + this.VenueID + '/false').subscribe({
      next: (response: any) => {
        if (response.succeeded) {
          this.packages = (response.data ?? [])
            .map((pkg: any) => ({ ...pkg, _bookingfeeid: pkg.bookingfeeid ?? null }))
            .sort((a: any, b: any) => (a.name ?? '').localeCompare(b.name ?? ''));
        } else {
          this.messageService.add({ severity: 'warn', summary: 'Warning', detail: response.messages?.[0] ?? 'Could not load packages' });
        }
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load packages' });
      }
    });
  }

  getFeeName(bookingfeeid: string | null): string {
    if (!bookingfeeid) return '';
    return this.feeNameMap.get(bookingfeeid) ?? this.feeDropdownOptions.find(f => f.value === bookingfeeid)?.label ?? '';
  }

  getFeeColour(bookingfeeid: string | null): string {
    if (!bookingfeeid) return 'transparent';
    return this.feeColourMap.get(bookingfeeid) ?? 'transparent';
  }

  onFeeChange(pkg: any): void {
    const updatedPkg = { ...pkg, bookingfeeid: pkg._bookingfeeid ?? null };
    this.busyService.busy();
    this.http.put(bmxurl + 'package', updatedPkg).subscribe({
      next: (response: any) => {
        this.busyService.idle();
        if (response.succeeded) {
          pkg.bookingfeeid = pkg._bookingfeeid;
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: `Booking fee updated for ${pkg.name}` });
        } else {
          pkg._bookingfeeid = pkg.bookingfeeid ?? null;
          this.messageService.add({ severity: 'error', summary: 'Not Saved', detail: response.messages?.[0] ?? 'Save failed' });
        }
      },
      error: () => {
        this.busyService.idle();
        pkg._bookingfeeid = pkg.bookingfeeid ?? null;
        this.messageService.add({ severity: 'error', summary: 'Not Saved', detail: 'Error saving booking fee assignment' });
      }
    });
  }
}
